/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { environment } from '../environments/environment'
import { SwUpdate } from '@angular/service-worker'
import { ActivatedRoute, Data, Router, RouterOutlet } from '@angular/router'
import { routeAnimations, UnespCoreMenuNavigationService, UnespCoreUserService } from 'src/libs/unesp-core'
import packageJson from '../../package.json'
import { AuthService } from './services/auth.service'
import { UserLoginService } from './services/usuario-login.service'
import { getMenu } from './menu'
import { Subscription } from 'rxjs'
import { filter, distinctUntilChanged, map } from 'rxjs/operators'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  version: string = packageJson.version
  env = environment
  fullTitle = `Sistema para Gestão de Concursos Públicos e Inscrições v${this.version}`
  shortTitle = `SINSC v${this.version}`
  footerText = 'DETIC - Diretoria Executiva de Tecnologia da Informação e Comunicação'

  private subscriptions: Subscription[] = []

  constructor(
    private swUpdate: SwUpdate,
    private authService: AuthService,
    private userLoginService: UserLoginService,
    private userService: UnespCoreUserService,
    private menuNavigationService: UnespCoreMenuNavigationService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(filter((evt) => evt.type === 'VERSION_READY')).subscribe(() => {
        if (confirm('Nova versão do sistema disponível. Deseja atualizar?')) {
          window.location.reload()
        }
      })
    }
    if (!this.env.production) {
      this.footerText = this.env.name
    }

    // Carrega user do localStorage para evitar flash de menu vazio
    this.userService.getUser()

    // Atualiza menu automaticamente quando usuário/roles mudam
    this.subscriptions.push(
      this.userService.user$.pipe(
        map(user => JSON.stringify(user?.roles ?? [])),
        distinctUntilChanged()
      ).subscribe(rolesJson => {
        const roles = JSON.parse(rolesJson) as string[]
        const isLoggedIn = roles.length > 0
        this.menuNavigationService.itensMenuSource.next(
          getMenu(!isLoggedIn, roles)
        )
      })
    )

    // Atualiza usuário do backend após cada refresh de token OAuth
    this.subscriptions.push(
      this.authService.tokenRefreshed$.pipe(
        filter(refreshed => refreshed)
      ).subscribe(() => this.syncUserFromBackend())
    )
  }

  private async syncUserFromBackend(): Promise<void> {
    try {
      const oldRoles = this.userLoginService.getUser()?.roles ?? []
      const user = await this.userLoginService.doLoginToken(this.authService.getAccessToken)

      // Verifica se perdeu role necessária para a rota atual
      if (user && this.hasLostRequiredRole(oldRoles, user.roles)) {
        this.router.navigate(['/access-denied'])
      }
    } catch (error) {
      console.error('Erro ao sincronizar usuário após refresh:', error)
    }
  }

  private hasLostRequiredRole(oldRoles: string[], newRoles: string[]): boolean {
    let route = this.activatedRoute
    while (route.firstChild) {
      route = route.firstChild
    }
    const allowedRoles: string[] = route.snapshot.data['allowedRoles'] ?? []

    if (allowedRoles.length === 0) return false

    const hadPermission = allowedRoles.some(role => oldRoles.includes(role))
    const hasPermission = allowedRoles.some(role => newRoles.includes(role))

    return hadPermission && !hasPermission
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  prepareRoute(outlet: RouterOutlet): Data {
    return outlet && outlet.activatedRouteData
  }
}
